// The library taken out as a CSV or a spreadsheet — one workspace's catalogue, or the whole
// organization's — the configurations saved for doing it, and the log every export leaves
// (`docs/plans/organization-exports.md`). Every export and every preset stands behind the
// operator's flag `assets.library_exports` (a `404` without it); the two logs stand behind none.
//
// A workspace export names its workspace and spends `assets.asset.export` there; the organization's
// takes `assets.library.export`. A workspace preset takes the workspace key, an organization-wide one
// `tenancy.settings.manage`.

import { api, withQuery, type FileBody } from '../client'
import type { components, operations } from '../schema'

export type ExportFieldEntry = components['schemas']['ExportFieldEntry']
export type ExportWorkspaceEntry = components['schemas']['ExportWorkspaceEntry']
export type ExportCount = components['schemas']['ExportCount']
export type ExportMode = components['schemas']['ExportMode']
export type ExportStatus = components['schemas']['ExportStatus']
export type ExportScope = components['schemas']['ExportScope']
/** The file an export is written as: `csv` — the default — or `xlsx`. */
export type ExportFormat = components['schemas']['ExportFormat']
export type ExportEntry = components['schemas']['ExportEntry']
/**
 * The period the statistics columns (`analytics_*`, `ratings_*`) are taken over: RFC 3339 instants,
 * each end optional, both included. Absent is the assets' whole lifetime.
 */
export type AnalyticsPeriod = components['schemas']['AnalyticsPeriod']
export type ExportFile = components['schemas']['ExportFile']
export type ExportRequester = components['schemas']['ExportRequester']
export type ExportLogPage = components['schemas']['Page_ExportEntry']
/** The walk: `limit`, `after`, and `order` — `desc` reads the newest export first. */
export type ExportLogQuery = NonNullable<operations['workspace_exports_list']['parameters']['query']>
export type ExportAssetsRequest = components['schemas']['ExportAssetsRequest']
export type OrganizationExportRequest = components['schemas']['OrganizationExportRequest']
export type ModerationStatus = components['schemas']['ModerationStatus']
/** Which end of a relation an asset must sit at — the search's relation criterion. */
export type RelationEnd = components['schemas']['RelationEnd']
export type ExportPresetEntry = components['schemas']['ExportPresetEntry']
export type CreateExportPresetRequest = components['schemas']['CreateExportPresetRequest']
export type UpdateExportPresetRequest = components['schemas']['UpdateExportPresetRequest']
export type ExportFieldsQuery = NonNullable<operations['exports_fields_list']['parameters']['query']>
export type ExportPresetsQuery = NonNullable<operations['export_presets_list']['parameters']['query']>

/**
 * What asking for a workspace export came to: the file itself, below the threshold, or — from it —
 * the export queued, as its log entry.
 */
export type ExportOutcome = { mode: 'sync'; file: FileBody } | { mode: 'async'; entry: ExportEntry }

/**
 * Every column an export can carry: the standard ones, then this organization's custom fields as
 * `meta_<slug>`, grouped by the schema defining them — with `workspace_id`'s own when one is named.
 * Without one it is exactly what the organization's export offers.
 */
export function listExportFields(query: ExportFieldsQuery = {}, signal?: AbortSignal): Promise<ExportFieldEntry[]> {
  return api.get<ExportFieldEntry[]>(withQuery('/exports/fields', query), undefined, signal)
}

/**
 * How many assets `request` would export, over exactly the criteria the export walks, and how it
 * would be answered: `mode` is `sync` below `async_threshold` — the file in the response — and
 * `async` from it — a file mailed when it is ready. `fields` is ignored.
 */
export function countExport(request: ExportAssetsRequest, signal?: AbortSignal): Promise<ExportCount> {
  return api.post<ExportCount>('/exports/count', request, undefined, signal)
}

/**
 * A workspace export, in the `format` asked for — a CSV when none is (UTF-8 with a byte-order mark,
 * `;` between cells), or an `.xlsx` spreadsheet — its statistics columns over `analytics_period`,
 * which their headings then name. A statistics column on a deployment with no analytics store is a
 * 503, an inverted period a 400. Below the threshold the answer is the file, named
 * by the server, and the export is on record once it is read to its end. From the threshold it is
 * queued (`202`): the file is written in the background, kept seven days behind a link only the
 * requester may follow, and mailed to them. An unknown field key is a 400 naming it.
 */
export async function createExport(request: ExportAssetsRequest): Promise<ExportOutcome> {
  const answer = await api.postFileOrAccepted<ExportEntry>('/exports', request)
  return answer.accepted ? { mode: 'async', entry: answer.body } : { mode: 'sync', file: answer.file }
}

/**
 * How many assets the organization's export would take out of every workspace — one row per asset,
 * pending and rejected placements included, the trash excluded. `mode` is always `async`.
 */
export function countOrgExport(request: OrganizationExportRequest, signal?: AbortSignal): Promise<ExportCount> {
  return api.post<ExportCount>('/orgs/current/exports/count', request, undefined, signal)
}

/**
 * Every live workspace of the organization, whether or not the caller holds a seat in it — the
 * choices the organization's export narrows to. The pool first, then by name; unpaged (the largest
 * tenant holds a few hundred). `asset_count` is what the export would carry from that workspace
 * alone, pending and rejected placements included. `assets.library.export`, behind the flag.
 */
export function listOrgExportWorkspaces(signal?: AbortSignal): Promise<ExportWorkspaceEntry[]> {
  return api.get<ExportWorkspaceEntry[]>('/orgs/current/exports/workspaces', undefined, signal)
}

/**
 * The organization's export, always queued: its log entry, at `queued`. The file is mailed to the
 * requester when it is ready. A column the organization lacks is a 400 naming it; a spreadsheet of
 * more assets than a sheet holds rows fails when it is written, and the mail says so.
 */
export function createOrgExport(request: OrganizationExportRequest): Promise<ExportEntry> {
  return api.post<ExportEntry>('/orgs/current/exports', request)
}

/**
 * One page of a workspace's export log — every export of it, whatever its mode or outcome, kept as
 * long as the organization, whether or not `assets.library_exports` is on. `download_url` is present on the
 * caller's own exports while the link lives; the address and the user agent are the organization's
 * log's alone.
 */
export function listWorkspaceExports(
  workspaceId: string,
  query: ExportLogQuery = {},
  signal?: AbortSignal,
): Promise<ExportLogPage> {
  return api.get<ExportLogPage>(
    withQuery(`/workspaces/${encodeURIComponent(workspaceId)}/exports`, query),
    undefined,
    signal,
  )
}

/**
 * One page of the organization's whole export log — every workspace's and the organization's own,
 * with the address and the user agent each came from. `assets.library.export`, no flag.
 *
 * `organizationId` asks about that organization rather than the one the page's `X-Org-Id` names,
 * for a read cached under the organization it was asked for.
 */
export function listOrgExports(
  query: ExportLogQuery = {},
  signal?: AbortSignal,
  organizationId?: string,
): Promise<ExportLogPage> {
  const headers = organizationId === undefined ? undefined : { 'X-Org-Id': organizationId }
  return api.get<ExportLogPage>(withQuery('/orgs/current/exports', query), headers, signal)
}

/** The saved configurations: the organization's, then `workspace_id`'s own when one is named. Unpaged. */
export function listExportPresets(query: ExportPresetsQuery = {}, signal?: AbortSignal): Promise<ExportPresetEntry[]> {
  return api.get<ExportPresetEntry[]>(withQuery('/export-presets', query), undefined, signal)
}

/** Keep a configuration, on a workspace or, with no `workspace_id`, the organization's. 409 a taken name. */
export function createExportPreset(request: CreateExportPresetRequest): Promise<ExportPresetEntry> {
  return api.post<ExportPresetEntry>('/export-presets', request)
}

/** Rename a preset, or replace what it configures. 409 a taken name. */
export function updateExportPreset(presetId: string, request: UpdateExportPresetRequest): Promise<ExportPresetEntry> {
  return api.patch<ExportPresetEntry>(`/export-presets/${encodeURIComponent(presetId)}`, request)
}

/** Forget a preset; its name is free again. */
export function deleteExportPreset(presetId: string): Promise<undefined> {
  return api.del<undefined>(`/export-presets/${encodeURIComponent(presetId)}`)
}
